import React, { useState, useEffect } from "react";
import { transferRuleService } from "../services/transferRuleService";
import { userService } from "../services/userService";

const TransferRulesManager = () => {
  const [roles, setRoles] = useState([]);
  const [transferRules, setTransferRules] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load roles and transfer rules from database
      const [rolesRes, rulesRes] = await Promise.all([
        userService.getRoles(), // Loads roles from database
        transferRuleService.getTransferRules(), // Loads transfer rules from database
      ]);

      setRoles(rolesRes.data.data);

      // Initialize rules matrix
      const rulesMap = {};
      rolesRes.data.data.forEach((fromRole) => {
        rulesMap[fromRole.role_id] = {};
        rolesRes.data.data.forEach((toRole) => {
          rulesMap[fromRole.role_id][toRole.role_id] = false;
        });
      });

      // Set existing rules
      rulesRes.data.data.forEach((rule) => {
        if (rule.allowed === 1) {
          rulesMap[rule.from_role_id][rule.to_role_id] = true;
        }
      });

      setTransferRules(rulesMap);
    } catch (error) {
      console.error("Error loading data:", error);
      alert(
        "Error loading data: " +
          (error.response?.data?.message || error.message)
      );
    }
    setLoading(false);
  };

  const toggleRule = (fromRoleId, toRoleId) => {
    setTransferRules((prev) => ({
      ...prev,
      [fromRoleId]: {
        ...prev[fromRoleId],
        [toRoleId]: !prev[fromRoleId][toRoleId],
      },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const rulesArray = [];
      Object.entries(transferRules).forEach(([fromRoleId, toRoles]) => {
        Object.entries(toRoles).forEach(([toRoleId, allowed]) => {
          rulesArray.push({
            from_role_id: parseInt(fromRoleId),
            to_role_id: parseInt(toRoleId),
            allowed: allowed ? 1 : 0,
          });
        });
      });

      const response = await transferRuleService.saveTransferRules({
        rules: rulesArray,
      });
      if (response.data.success) {
        alert("Transfer rules saved successfully!");
      } else {
        alert(response.data.message || "Error saving transfer rules");
      }
    } catch (error) {
      console.error("Error saving transfer rules:", error);
      alert(
        "Error saving transfer rules: " +
          (error.response?.data?.message || error.message)
      );
    }
    setSaving(false);
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="p-8 ">
      <div className="max-w-6xl mx-auto p-8">
        <h2 className="text-2xl font-bold mb-4">Transfer Rules Management</h2>
        <p className="text-gray-600 mb-6">
          Configure which roles can transfer applications to other roles.
        </p>

        <div className="bg-white rounded-lg shadow-md p-6 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gradient-to-r from-teal-600 to-emerald-600 p-0">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                  From Role
                </th>
                {roles.map((role) => (
                  <th
                    key={role.role_id}
                    className="px-6 py-3 text-center text-xs font-medium text-white uppercase tracking-wider"
                  >
                    {role.role_name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {roles.map((fromRole) => (
                <tr key={fromRole.role_id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {fromRole.role_name}
                  </td>
                  {roles.map((toRole) => (
                    <td
                      key={toRole.role_id}
                      className="px-6 py-4 whitespace-nowrap text-center"
                    >
                      <input
                        type="checkbox"
                        checked={
                          transferRules[fromRole.role_id]?.[toRole.role_id] ||
                          false
                        }
                        onChange={() =>
                          toggleRule(fromRole.role_id, toRole.role_id)
                        }
                        disabled={fromRole.role_id === toRole.role_id}
                        className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-6">
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save Transfer Rules"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransferRulesManager;
